import {
  Aplicacao,
  AplicacaoStatusProcessando,
  AqilaRecomendacao,
  Recomendacao,
  TypedAplication,
} from '../../@types';

export type PendingAproveMainObject = {
  cultura: string | undefined;
  areaAplicada: number | null;
  dataInicial: string;
  dataFinal: string;
  operador: string | null;
  recomendante: string | null;
  talhoes: { areaRecomendada: string; descricao: string }[] | null;
  propriedade: string;
  propriedadeId: number;
  gleba: string | null;
  usuario: string;
  tipoOperacao: string;
  isRecomendacao: boolean;
  statusProcessando: AplicacaoStatusProcessando | false | null;
  type: 'recomendacoes' | 'aplicacoes' | 'aqilaRecomendacoes';
  estadio: { abreviatura: string; descricao: string } | null;
  nrTanques: number | null;
  obs: string | null;
};

function buildFromRecomendacao(
  item: Recomendacao & { type: 'recomendacoes' },
): PendingAproveMainObject {
  return {
    cultura: item.atividade.descricao,
    areaAplicada: null,
    dataInicial: item.dataInicio,
    dataFinal: item.dataTermino,
    operador: null,
    recomendante: item.recomendante.nome,
    talhoes: item.glebas.map((g) => ({
      areaRecomendada: g.areaTotal,
      descricao: g.descricao,
    })),
    propriedade: item.propriedade.descricao,
    propriedadeId: item.propriedade.id,
    gleba: null,
    usuario: item.recomendante.nome,
    tipoOperacao: 'Recomendação de Defensivos',
    isRecomendacao: true,
    statusProcessando: false,
    type: 'recomendacoes',
    estadio: null,
    nrTanques: null,
    obs: null,
  };
}

function buildFromAplicacao(
  item: Aplicacao & { type: 'aplicacoes' },
): PendingAproveMainObject {
  return {
    cultura: item.cultura.descricao,
    areaAplicada: item.auditoriaAplicacao.area,
    dataInicial: item.auditoriaAplicacao.dtIni,
    dataFinal: item.auditoriaAplicacao.dtFim,
    operador: item.usuario.nome,
    recomendante: null,
    talhoes: null,
    propriedade: item.propriedade.descricao,
    propriedadeId: item.propriedade.id,
    gleba: item.gleba.descricao,
    usuario: item.usuario.nome,
    tipoOperacao: item.tipoOperacao,
    isRecomendacao: false,
    statusProcessando: item.statusProcessando,
    type: 'aplicacoes',
    estadio: item.estadio ?? null,
    nrTanques: item.auditoriaAplicacao.nrTanques ?? null,
    obs: item.auditoriaAplicacao.obs || null,
  };
}

const AQILA_RECOMENDACAO_TIPO_OPERACAO: Record<number, string> = {
  3: 'Recomendação de Adubação',
  11: 'Recomendação de Correção de Solo',
};

function buildFromAqilaRecomendacao(
  item: AqilaRecomendacao & { type: 'aqilaRecomendacoes' },
): PendingAproveMainObject {
  return {
    cultura: item.atividade?.descricao,
    areaAplicada: null,
    dataInicial: item.dataInicio,
    dataFinal: item.dataTermino,
    operador: item.usuario.nome,
    recomendante: item.recomendante.nome,
    talhoes: item.glebas.map((g) => ({
      areaRecomendada: String(g.gleba.areaTotal),
      descricao: g.gleba.descricao,
    })),
    propriedade: item.propriedade.descricao,
    propriedadeId: item.propriedade.id,
    gleba: null,
    usuario: item.recomendante.nome,
    tipoOperacao:
      AQILA_RECOMENDACAO_TIPO_OPERACAO[item.operacao.id] ?? 'Recomendação',
    isRecomendacao: true,
    statusProcessando: item.statusProcessando ?? null,
    type: 'aqilaRecomendacoes',
    estadio: null,
    nrTanques: null,
    obs: null,
  };
}

export function buildPendingAproveMainObject(
  item: TypedAplication,
): PendingAproveMainObject {
  if (item.type === 'recomendacoes') {
    return buildFromRecomendacao(item);
  }

  if (item.type === 'aqilaRecomendacoes') {
    return buildFromAqilaRecomendacao(item);
  }

  return buildFromAplicacao(item);
}
